import { ref } from 'vue';

export type AppTheme =
  | 'light'
  | 'dark'
  | 'nord'
  | 'matcha'
  | 'terracotta'
  | 'orchid'
  | 'aurora'
  | 'espresso'
  | 'burgundy'
  | 'neon-noir';

export type ArtworkWashGroup = 'light' | 'dark';

export const themeOptions: ReadonlyArray<{
  value: AppTheme;
  label: string;
  group: 'Base' | 'Light palettes' | 'Dark palettes';
}> = [
  { value: 'light', label: 'Light', group: 'Base' },
  { value: 'dark', label: 'Dark', group: 'Base' },
  { value: 'nord', label: 'Nord', group: 'Light palettes' },
  { value: 'matcha', label: 'Matcha', group: 'Light palettes' },
  { value: 'terracotta', label: 'Terracotta', group: 'Light palettes' },
  { value: 'orchid', label: 'Orchid', group: 'Light palettes' },
  { value: 'aurora', label: 'Aurora', group: 'Dark palettes' },
  { value: 'espresso', label: 'Espresso', group: 'Dark palettes' },
  { value: 'burgundy', label: 'Burgundy', group: 'Dark palettes' },
  { value: 'neon-noir', label: 'Neon Noir', group: 'Dark palettes' },
];

const darkThemes = new Set<AppTheme>(['dark', 'aurora', 'espresso', 'burgundy', 'neon-noir']);
const isAppTheme = (value: string | null): value is AppTheme =>
  themeOptions.some((theme) => theme.value === value);

const THEME_STORAGE_PREFIX = 'geobu:theme';
const ARTWORK_WASH_STORAGE_PREFIX = 'geobu:artwork-wash';
const defaultArtworkWashOpacity: Record<ArtworkWashGroup, number> = { light: 80, dark: 60 };

export const activeTheme = ref<AppTheme>('light');
export const lightArtworkWashOpacity = ref(defaultArtworkWashOpacity.light);
export const darkArtworkWashOpacity = ref(defaultArtworkWashOpacity.dark);

const storageKey = (userId?: string | null) => `${THEME_STORAGE_PREFIX}:${userId || 'anonymous'}`;
const artworkWashStorageKey = (group: ArtworkWashGroup, userId?: string | null) =>
  `${ARTWORK_WASH_STORAGE_PREFIX}:${group}:${userId || 'anonymous'}`;

const readTheme = (userId?: string | null): AppTheme => {
  try {
    const stored = localStorage.getItem(storageKey(userId));
    return isAppTheme(stored) ? stored : 'light';
  } catch {
    return 'light';
  }
};

const readArtworkWashOpacity = (group: ArtworkWashGroup, userId?: string | null) => {
  try {
    const storedValue = localStorage.getItem(artworkWashStorageKey(group, userId));
    if (storedValue === null) return defaultArtworkWashOpacity[group];
    const stored = Number(storedValue);
    return Number.isFinite(stored) && stored >= 0 && stored <= 100
      ? stored
      : defaultArtworkWashOpacity[group];
  } catch {
    return defaultArtworkWashOpacity[group];
  }
};

export const artworkWashGroupForTheme = (theme: AppTheme): ArtworkWashGroup =>
  darkThemes.has(theme) ? 'dark' : 'light';

const applyToDocument = (theme: AppTheme) => {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = darkThemes.has(theme) ? 'dark' : 'light';
  const opacity =
    artworkWashGroupForTheme(theme) === 'dark'
      ? darkArtworkWashOpacity.value
      : lightArtworkWashOpacity.value;
  document.documentElement.style.setProperty('--ui-artwork-wash-opacity', String(opacity / 100));
};

export const initializeTheme = (userId?: string | null) => {
  activeTheme.value = readTheme(userId);
  lightArtworkWashOpacity.value = readArtworkWashOpacity('light', userId);
  darkArtworkWashOpacity.value = readArtworkWashOpacity('dark', userId);
  applyToDocument(activeTheme.value);
};

export const setTheme = (theme: AppTheme, userId?: string | null) => {
  activeTheme.value = theme;
  try {
    localStorage.setItem(storageKey(userId), theme);
  } catch {
    // ignore
  }
  applyToDocument(theme);
};

export const setArtworkWashOpacity = (
  group: ArtworkWashGroup,
  value: number,
  userId?: string | null
) => {
  const opacity = Math.min(100, Math.max(0, Math.round(value)));
  const target = group === 'dark' ? darkArtworkWashOpacity : lightArtworkWashOpacity;
  target.value = opacity;
  try {
    localStorage.setItem(artworkWashStorageKey(group, userId), String(opacity));
  } catch {
    // ignore
  }
  if (artworkWashGroupForTheme(activeTheme.value) === group) {
    applyToDocument(activeTheme.value);
  }
};

export function useTheme() {
  return {
    activeTheme,
    themeOptions,
    lightArtworkWashOpacity,
    darkArtworkWashOpacity,
    artworkWashGroupForTheme,
    initializeTheme,
    initTheme: initializeTheme,
    setTheme,
    setArtworkWashOpacity,
    isDark: () => darkThemes.has(activeTheme.value),
  };
}
